import {css, html, nothing} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {of, filter, fromEvent, map, NEVER, Observable, tap} from 'rxjs';
import {Activity} from './activity';
import {ActivityOption} from './activity-option';
import {ActivityQueueItem, Configuration} from './activity-queue-item';
import Decimal from './break_infinity';
import {observe} from './directives/observe';
import global_css from './global-css';
import {evaluateCost} from './interfaces/dynamic-value';
import {ri} from './runner-idle';
import {ActivityName, Gettable} from './types/runner-idle';
import {formatCostEffect, formatEng, formatTime} from './utils';

@customElement('ri-spell')
export class Spell extends Activity {
  // #region Properties (22)

  public override toJSON(): {} {
    return {...super.toJSON(), currentCooldown: this.currentCooldown.ceil()};
  }

  public static override styles = [
    global_css,
    css`
      :host {
        width: 8em;
        text-align: center;
      }
      .container {
        border: 1px black solid;
        padding: 4px;
      }
      div.drag,
      div.drag-helper.dragging {
        background-color: rgb(var(--value-color, initial), 1);
        font-size: 1rem;
        border: 1px solid black;
        border-radius: 4px;
        margin: 2px;
        padding: 2px 4px;
      }

      div.drag {
        transition-duration: 0.25s;
        cursor: move;
      }

      div.drag:hover {
        background-color: #0008;
        color: white;
      }

      div.drag-helper {
        display: none;
      }

      div.drag-helper.dragging {
        display: block;
        cursor: move;
      }

      .cooldown,
      .cannot-afford {
        opacity: 0.5;
      }
    `,
  ];

  @property({type: Boolean, reflect: true}) public active = false;
  // HACK
  @property() public override displayName = 'Spell';
  @property({type: Number}) public spellLevel = 1;
  @property() public override name = 'spell' as ActivityName;
  @property() public override onPerform: (
    step: number,
    optionIndex: number
  ) => void = (_step, _optionIndex) => {
    // Do nothing.
  };
  @property({type: Number}) public override optionIndex = 0;
  public override canAfford(
    step: number,
    optionIndex: number,
    chanceRoll: number,
    dryRun?: boolean
  ): boolean {
    return (
      super.canAfford(step, optionIndex, chanceRoll, dryRun) &&
      this.currentCooldown.lte(0.01)
    );
  }
  @property() public override perform: (
    step: number,
    optionIndex: number,
    configuration: Configuration
  ) => boolean = (step, optionIndex, configuration) => {
    console.log(`%cPERFORM ${this.name}`, 'font-weight: bold');
    const chanceRoll = Math.random();
    if (this.canAfford(step, optionIndex, chanceRoll)) {
      const optionOverride = this.checkForOptionOverride(
        step,
        optionIndex,
        chanceRoll
      );
      const cost = optionOverride.cost ?? this.cost;
      const effect = optionOverride.effect ?? this.effect;
      // // Boost INT when casting spells.
      // ri.stats.intelligence.value = ri.stats.intelligence.value.plus(
      //   this.tags.has('manual') ? 0.01 : 0.001 * Math.pow(2, this.level - 1)
      // );
      // // Boost Magic for unaligned Spells
      // if (![...this.tags].some((tag) => tag.includes('Magic'))) {
      //   ri.stats.magic.value = ri.stats.magic.value.plus(
      //     this.tags.has('manual') ? 0.01 : 0.001 * Math.pow(2, this.level - 1)
      //   );
      // }
      // Reset cooldown
      this.currentCooldown = this.cooldown;
      // Update magic stats
      for (const tag of this.tags) {
        if (tag.includes('Magic')) ri.gain(tag as Gettable, 0.01);
      }

      const evaluatedCost = evaluateCost(
        cost,
        step,
        optionIndex,
        configuration
      );
      const evaluatedEffect = evaluateCost(
        effect,
        step,
        optionIndex,
        configuration
      );

      ri.logCostEffect(this, '', evaluatedCost, evaluatedEffect, ['spell']);
      console.log(
        `costs for ${this.name}: ${Array.from(
          evaluatedCost,
          ([k, v]) => `${k}: ${v.value}`
        ).join(', ')}`
      );
      // Pay all the costs
      for (const [key, {value}] of evaluatedCost) {
        ri.gain(key, -value);
      }
      console.log(
        `effects for ${this.name}: ${Array.from(
          evaluatedEffect,
          ([k, v]) => `${k}: ${v.value}`
        ).join(', ')}`
      );
      // Perform the value-based effects.
      for (const [key, {value}] of evaluatedEffect) {
        ri.gain(key, value);
      }
      // Perform anything that can't be done in the value-based effects.
      this.onPerform(step, optionIndex);
      this.uses$.next(this.uses$.value.plus(1));
      return true;
    } else {
      // Can't afford it.
      this.onCannotAfford(step, optionIndex);
      return false;
    }
    throw new Error('Something is wrong in this method.');
  };
  @state() public cooldown: Decimal = new Decimal(1);

  public _currentCooldown: Decimal = new Decimal(0);
  public override baseTags = new Set(['spell']);
  /** A stream signaling requests to manually cast this. */
  public requestCast$: Observable<Activity> = this.leftClick$.pipe(
    // No modifier keys
    filter(
      (event) =>
        !(event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
    ),
    map(() => this)
  );
  public override cost = new Map();
  public defaultWidth = 0;
  public override effect = new Map();
  public override enqueue$!: Observable<Activity>;
  public getCostString = () => '';
  public getEffectString = () => '';

  public get currentCooldown(): Decimal {
    return this._currentCooldown;
  }

  public set currentCooldown(value: Decimal) {
    this._currentCooldown = value.clamp(0, this.cooldown);
    this.requestUpdate();
  }

  // #endregion Properties (22)

  // #region Constructors (1)

  constructor() {
    super();
    setTimeout(() => {
      this.classList.add(this.name, ...this.tags);
    });
    this.enqueue$ = this.leftClick$.pipe(
      filter(() => !this.tags.has('manual')), // TODO replace
      // Shift click
      filter((event) => !(event.ctrlKey || event.metaKey) && event.shiftKey),
      map(() => this)
    );
  }

  // #endregion Constructors (1)

  // #region Public Methods (5)

  public override _updateTooltip() {
    let costEffectString;
    if (this.options.length > 0) {
      costEffectString = '<div class="options-container">';
      for (const optionName of this.options) {
        const option: ActivityOption = this.optionsData[optionName];
        if (option) {
          costEffectString += `<br>${option.displayName}<br>${formatCostEffect(
            {...option, duration: option.duration ?? this.duration},
            1
          )}<br>`;
        }
      }
      costEffectString += '</div>';
    } else {
      costEffectString = formatCostEffect(this, 1);
    }
    this.tooltip?.setContent(
      `${this.description}<br><br>${costEffectString}${
        this.tooltipContent() ? '<br><br>' + this.tooltipContent() : ''
      }
      <br><br>Cooldown: ${formatTime(this.currentCooldown)}/${formatTime(
        this.cooldown
      )}
      <br>Uses: ${this.uses$.value}<br>Time Spent: ${formatTime(
        this.timeSpent
      )}<br>Tags: ${[...this.baseTags]}`
    );
  }

  // @property() override canAfford: (
  //   step: number,
  //   optionIndex: number
  // ) => boolean = (step, optionIndex) => {
  //   for (const [key, {value, string}] of this.cost) {
  //     if (
  //       new Decimal(value(step, optionIndex)).gt(
  //         (ri.get(key) as IValuable).value
  //       )
  //     ) {
  //       return false;
  //     }
  //   }
  //   return true;
  // };
  public cast(step: number, optionIndex: number, configuration: Configuration) {
    this.perform(step, optionIndex, configuration);
  }

  public override setup(overrides: Partial<Spell>): void {
    super.setup(overrides);
    this.requestCast$.subscribe(() =>
      this.cast(0, this.optionIndex, this.configuration)
    );
  }

  public override render() {
    return html`
      <div
        class="chip spell fancy ${this.name}
          ${this.canAfford(-1, 0, 1, true) ? '' : 'cannot-afford'}
          ${this.currentCooldown.gt(0) ? 'cooldown' : ''}"
      >
        ${this.currentCooldown.gt(0)
          ? formatEng(this.currentCooldown, 1)
          : `LVL${this.level} ${this.displayName}`}
        ${this.options.length > 0
          ? html`
              <select
                name="options"
                class=${this.options[this.optionIndex]}
                @click=${(e: Event) => e.stopPropagation()}
                @change=${(e: Event) =>
                  (this.optionIndex = Number(
                    (e.target as HTMLOptionElement).value
                  ))}
              >
                ${this.options.map(
                  (option, index) => html`
                    <option
                      value=${index}
                      class=${this.options[index]}
                      ?selected=${index == this.optionIndex}
                    >
                      ${option}
                    </option>
                  `
                )}
              </select>
            `
          : nothing}
      </div>
    `;
  }

  // #endregion Public Methods (5)
  // enqueue(): void {
  //   // this.cast(ri, 0, 0);
  //   const thisQueueItem = document.createElement(
  //     'ri-activity-queue-item'
  //   ) as ActivityQueueItem;
  //   thisQueueItem.activity = this;
  //   thisQueueItem.option =
  //     thisQueueItem.activity.options[thisQueueItem.activity.optionIndex];
  //   if (ri.lastGameSpeed) {
  //     ri.gameSpeed = ri.lastGameSpeed;
  //     ri.lastGameSpeed = null;
  //   }
  //   ri.addToQueue(thisQueueItem);
  //   ri.requestUpdate();
  // }
}
